import { config } from '@/config';
import COLORS from '@/design-system/colors.json';

export default {
  datePicker: {
    today: 'Today',
    date: 'Date',
    days: 'Days',
    last: 'Last',
    custom: 'Custom',
    apply: 'Apply',
    from: 'From',
    to: 'To',
  },
  text: {
    test: 'test | tests | {count} tests',
  },
  incompatibleBrowser: {
    unsupportedBrowser: 'This browser is not supported by Incredibuild and may result in unexpected behavior.',
    unsupportedVersion: 'This version of Chrome is not supported and may result in unexpected behavior.',
  },
  login: {
    loginForm: {
      title: 'Welcome',
      subtitle: 'Login to turbocharge your development cycles',
      username: {
        text: '',
        placeholder: 'Username',
        requiredFieldValidation: 'Username is required',
        inputValidation: 'Must be at least 4 characters, with no special characters or spaces',
      },
      password: {
        text: '',
        placeholder: 'Password',
        requiredFieldValidation: 'Password is required',
        inputValidation: 'Must be at least 8 characters, with at least one uppercase letter',
      },
      validationCredentials: {
        wrongUserPass: `Username or password incorrect,<br>please try again`,
        lockWarning: `Repeated incorrect entries will<br> lock your user`,
        lockedUser: `Too many failed attempts.<br> Your account has been locked, please contact your administrator to release it`,
        inactiveUser: `Your account has been deactivated,<br> please contact your administrator to release it`,
        localUserNotAllowed: 'An integration with an authentication system is enabled, therefore logging in is only supported through this integration',
        invalidAuthGroupMapping: 'This user does not have permission to log in. Incredibuild did not locate a Security Group with this user',
        ldapMachineIsDown: "We're unable to connect to your authentication system at this time",
      },
      forgotPassword: {
        text: 'Forgot Password?',
      },
      submitLabel: 'Login',
      supportLink: 'Contact Support',
      dialog: {
        forgotPassword: {
          title: 'Forgot Username or Password',
          body: () => `
          <div class="gordita-regular-14" style="color: ${COLORS['Text/text_medium']}">
            <span>To change your user/password please refer to our documentation</span>
            <a class="gordita-500-14-m" style="color: ${COLORS['System/app_primary']}" target="_blank" href=https://$LINK>here</a>
            <span>or contact support at</span>
            <a class="gordita-500-14-m" style="color: ${COLORS['System/app_primary']}" href=mailto:${config?.value?.general?.supportMail?.split(':')[1]}>${
            config?.value?.general?.supportMail?.split(':')[1]
          }</a>
          </div>`,
        },
      },
      licenseUploadFlow: {
        error: 'It looks like something went wrong while trying to validate your license. Please contact your customer success Manager.',
        generateLicenseForm: {
          title: {
            A: 'Generate',
            B: 'License Key',
          },
          subtitle: {
            A: 'After generating a license key, check your email and send the key to ',
            B: "support{'@'}incredibuild.com",
            C: ' and we will send you a license file.',
            D: 'If you already received a license file, you can skip this step.',
          },
        },
        uploadLicenseForm: {
          title: 'Upload License',
          subtitle: 'Upload the license file you received from Incredibuild',
        },
        expiredLicenseForm: {
          title: 'License Expired',
          subtitle: {
            A: 'Contact your Customer',
            B: 'Success Manager to renew',
            C: 'your license today.',
          },
        },
      },
    },
    licenseForm: {
      title: 'Welcome',
      renewLicenseDialogTitle: 'Renew License',
      communicationIssueTitle: "Couldn't connect",
      titleExpired: 'Oh No!',
      firstLogin: {
        subtitle: {
          A: 'Enter a license key to continue.',
          B: 'If you don’t have a license key,',
          C: 'please contact support.',
        },
      },
      subtitle: {
        A: 'You should have received a key',
        B: ' in your email ',
        C: '',
      },
      subtitleExpired: {
        A: 'Your license key has expired. ',
        B: 'Please contact Customer Success to get a new license key',
        expired: 'Your license key has expired.Please contact Customer Success to get a new license key',
        locked: 'Unfortunately your coordinator has locked',
      },
      licenseInput: {
        text: 'Enter your license key',
        textDialog: 'Enter a valid license key',
        placeholder: 'License key',
        validation: 'License key is required',
        validationBadLicense: 'License key isn’t correct',
        validationBadLicenseExpired: 'Failed: The entered license has expired',
        validationBadLicenseNetworkFailed: 'Failed: Could not connect to the server.',
      },
      submitLabel: 'Login',
      submitLabelDialog: 'Load key',
      submitLabelSecondary: 'Refresh License',
      renewTitle: 'If you recently renewed your license, click refresh',
      renewTitleDialog: {
        A: 'If you recently renewed you old key, ',
        B: 'refresh now',
        C: '.',
      },
      renewErrorDialog: 'Your license key was not updated or failed to load.',
      supportLink: 'Contact Support',
      supportLinkDialog: 'Need Help?',
    },
    licenseError: {
      title: "Couldn't Connect",
      subtitle: 'You might want to check if there has been a recent change to your firewall or port settings',
      submitLabel: 'Try Again',
      supportLink: 'Need Help?',
    },
    licenseLocked: {
      title: 'Oh No!',
      subtitle: 'Something went wrong',
      subtitle2: 'Please try again later or contact us',
      submitLabel: 'Try Again',
      supportLink: 'Need Help?',
    },
    failed: 'Login failed - wrong credentials',
    networkError: {
      title: 'Coordinator lost communication',
      subtitle: 'Try restart IB coordinator from Task Manager > Services, if this doesn’t help, please contact support',
      submitLabel: 'Retry',
    },
  },
  generic: {
    submit: 'Submit',
    next: 'Next',
    copiedToClipboard: 'Copied to clipboard',
    linkDown: {
      title: 'Coordinator lost communication.',
      body: 'Please navigate to the Task manager > Services and restart all Incredibuild services that are down. if this doesn’t help, please contact support',
      serviceDown: {
        title: 'The license service on your machine is disabled or not working.',
        body: "If you cannot activate it, contact support{'@'}incredibuild.com",
      },
      networkFailure: {
        title: 'Failed to communicate with the license server.',
        body: "Refresh and try again, or contact support{'@'}incredibuild.com if the issue persists.",
      },
      unexpectedError: {
        title: 'Unexpected error, please refresh to continue',
      },
      trialExpired: {
        title: 'We noticed that you have already used a free trial.',
        body: "We would love to speak to you about getting a standard license or to discuss any questions you might have. Contact us at sales{'@'}incredibuild.com",
      },
    },
    coordinatorUpTime: {
      runningSince: 'Running since ',
    },
    backupCoordinator: {
      statusTooltip: {
        incompatible: 'Please upgrade to Primary Coordinator version',
        notConfigured: 'Go to Settings > General > Backup',
        unreachable: 'Backup coordinator is unreachable, please check your connection and make sure that the certificate you’ve provided is valid',
      },
      coordinatorType: {
        primary: 'Primary Coordinator',
        configuredBackup: {
          windows: 'Backup Coordinator',
          linux: 'Secondary Coordinator',
        },
        backups: '{0} Backup Coordinators',
      },
      statusTextByType: {
        active: 'active',
        notActive: 'not active',
        standby: 'on standby',
        incompatible: 'incompatible',
        notConfigured: 'not configured',
        unreachable: 'unreachable',
      },
      statusTypes: {
        active: 'Active',
        standBy: 'Standby',
        notConfigured: 'NotConfigured',
        incompatible: 'Incompatible',
        unreachable: 'Unreachable',
      },
      dialog: {
        // when backup is not active
        primary: {
          title: 'Primary Coordinator Is Up',
          body: 'Main Coordinator is running again. We are redirecting you to your Main Coordinator',
          okLabel: 'OK',
        },
        // when backup is active
        backup: {
          title: 'Primary Coordinator Is Down',
          body: "Don't worry, your builds are still running. Do you want to open the Backup coordinator manager? ",
          okLabel: 'Redirect me',
          cancelLabel: 'Stay here',
        },
      },
    },
    licenseStates: {
      enterLicenseKey: 'Enter license key',
      loadKey: 'Load Key',
      retry: 'Retry',
      refreshNow: 'refresh now',
      activateNewLicense: 'To activate a new license, you need to deactivate your current one',
      licenseMarketPlace: {
        title: 'New Package Required',
        description: 'You can update your Incredibuild Cloud Package in ',
      },
      licenseNoneNotFound: {
        title: 'License Not Found',
        loginManualRefreshDesc: 'The license key does not exist, try to re-enter your license key or contact ',
        heartbeatDescription: {
          a: 'The license key does not exist.',
          b: 'Contact',
          c: ' or try again',
        },
      },
      licenseExpired: {
        title: 'License Expired',
        description: {
          a: 'Contact your Customer Success Manager or ',
          b: ' to renew your license today.',
        },
        footerText: 'If you recently renewed your current license,',
        isBackupDescription:
          'License activation is available only from your primary coordinator. If you renewed your license or you wish to activate a new license, you must login to your primary coordinator first',
      },
      licenseDisabled: {
        title: 'License Revoked',
        description: {
          a: 'It looks like your licenses was revoked by Incredibuild.',
          b: 'Please contact ',
          c: ' for more information.',
        },
        footerText: 'If this issue was resolved,',
      },
      licenseFailure: {
        title: 'License Validation Failed',
        description: 'It looks like something went wrong while trying to validate your license. Please try again or contact',
        errorDetails: 'Error Details',
      },
      licenseTrialFailure: {
        title: 'License Activation Failed',
        description: 'You must really like Incredibuild if you are trying to install our trial license again!',
      },
      licenseOccupied: {
        title: 'License Already in Use',
        description: 'This license key is already activated on another coordinator machine. To switch the license to this machine, deactivate it on the other machine first',
      },
      licenseLocked: {
        title: 'License Locked',
        description: 'We haven’t been able to connect to the license server to validate your license in a while. Make sure all components can communicate with each other',
        footerText: 'If this issue was resolved,',
      },
    },
    userNotifications: {
      1: {
        type: 'licenseUpdate',
        title: 'Updated License Package',
        content: 'Your license package has been updated. <br/> This may affect the number of available licenses.',
      },
      2: {
        type: 'newVersion',
        title: 'Version Update',
        content: () => `Version {0} is now available. <br/> <a target="_blank" style="color: ${COLORS['System/app_primary']}" href=${process.env.VUE_APP_DOWNLOAD_VERSION}>Download it</a> Now.`,
      },
      3: {
        type: 'licenseLockUpdate',
        title: 'WARNING: License Communication Failed',
        content: 'The Coordinator has failed to verify your license because it cannot connect to the license server. Unless resolved, you will be locked out of the Coordinator.',
      },
      4: {
        type: 'licenseAboutToBeExpired',
        title: 'Your License Is About To Expire',
        content: () =>
          `We hope you are enjoying the benefits of Incredibuild! We just want to remind you that your license will expire on <span class="ibf-gordita-12-m" style="color: ${COLORS['Text/Dark']}">{0}</span>. If you already renewed your license, please ignore this message.`,
      },
    },
    emptyPlaceholder: {
      noAgentsFound: {
        a: 'There are no Agents currently',
        b: 'connected to this Coordinator',
      },
    },
    time: {
      lessThanSecond: '<1s',
    },
    scl: 'Smart compatibility layer',
    sclTitleForLinux: 'Linux',
  },
  dashboard: {
    tabs: {
      workloadOverview: {
        title: 'Builds Overview',
      },
      gridUtilization: {
        title: 'Grid Utilization',
      },
    },
    datePicker: {
      notes: {
        input: 'Please choose up to 30 days',
      },
    },
    infoChart: {
      activeCores: {
        legend: {
          helperCores: 'Helper Cores',
          initiatorCores: 'Initiator Cores',
        },
      },
    },
    top: {
      topLeftGadgets: {
        tooltip: {
          title: 'Online Agents Activity',
          content: {
            a: 'A summary of your Incredibuild online agents. This includes agents with a valid license that are currently working or ready to participate in builds.',
            b: 'Online Agent Machines',
            c: 'A breakdown of the total number of online agent machines, both on-Prem and cloud (if exists).',
            d: 'Busy Machines - This represents the machines that are online and currently participating in a build. ',
            e: 'Idle Machines - On-Prem machines that are available in your grid but aren’t participating in a build.',
            f: 'Busy Cores',
            g: 'The total number of busy cores currently working.',
            h: 'Helper Cores - the number of remote cores that are currently assisting to builds, this includes both On-Prem and cloud cores.',
            i: 'Initiator Cores - the total number of local cores, on the initiator machines, that are currently executing builds.',
            j: 'Active Builds',
            k: 'The number of builds that are being executed at this moment.',
          },
        },
        agents: { title: 'Online Agent Machines', activeLabel: 'Active', idleLabel: 'Idle' },
        initiators: { title: 'Active Initiators' },
        helpers: { title: 'Active Helper Cores' },
      },
      topRightBars: {
        tooltip: {
          title: 'Total License Usage',
          subtitle: 'Displays how many licenses you have of each type, and how they are currently utilized.',
          helperCoresTooltip: 'The number of online cores assigned with a Floating Helper role that can participate in builds',
          initiatorLicenses: {
            title: 'Initiator',
            subtitle: 'The total number of Initiator licenses.',
            content: {
              a: 'Assigned',
              b: 'Licenses that are assigned to an Agent.',
              c: 'Cooldown',
              d: 'Each fixed initiator license can only be assigned once per 24 hours. After each assignment, the license enters a cooldown period of 24 hours.',
              e: 'Available',
              f: 'Licenses that are not assigned to any Agent.',
            },
          },
          helperCores: {
            title: 'Helper Cores',
            subtitle: 'The total number of Helper Core licenses.',
            content: {
              a: 'Assigned',
              b: 'For fixed licenses, the number of licenses assigned to an Agent.',
              c: 'Available',
              d: 'For fixed licenses, the number of licenses that are not assigned to any Agent.',
              e: 'Helper Core Pool',
              f: 'The current number of online Helper Cores that have been assigned a floating license. You can add as many Helper Cores to this pool as you want, and in general, the Helper Core Pool should be greater than the total number of Helper Core licenses.',
            },
          },
          buildCache: {
            title: 'Build Cache Capacity',
            subtitleA: 'Used',
            subtitleB: 'Available',
            subtitleC: 'Next Reset',
            content: {
              mainDescription:
                'The hourly amount of build cache usage defined in your license package. Measured in cache cores, which are defined as how many hours of computing time on a single core were saved by accessing the cache.',
              descriptionA: 'The number of cache cores saved this hour by using build cache.',
              descriptionB: 'The number of cache cores left in your license package. This is reset every hour. ',
              descriptionC: 'The time until your build cache capacity will be reset and fully available. ',
            },
          },
        },
        totalInitiators: { title: 'Initiators' },
        totalHelpers: { title: 'Helper Cores' },
        totalBuildAvoidance: { title: 'Build Cache' },
        legend: { assigned: 'Assigned', cooldown: 'Cooldown', available: 'Available', used: 'Used', reset: 'Next Reset' },
        coolDownTooltip: 'Next license will be released in',

        cloudLicense: {
          cloudExpired: {
            title: 'Cloud Expired',
            icon: 'cloud-expired',
            description: 'Contact your Customer Success Manager to get up and running again',
            tooltip: 'Contact your Customer Success Manager to get up and running again',
          },
          cloudLocked: {
            title: 'Cloud Locked',
            icon: 'cloud-locked',
            description: 'Your cloud account has been locked. Contact your Customer Success Manager',
            tooltip: 'Your cloud account has been locked.\nContact your Customer Success Manager',
          },
          cloudTrial: {
            title: 'Cloud Hours Used',
            description: 'Expiration Date',
            tooltip: 'Expiration Date',
          },
          cloudUnavailable: {
            title: 'Data Unavailable',
            icon: 'cloud-unavailable',
          },
        },
      },
    },
    empty: {
      title: 'Go ahead! Start Adding to your Dashboard',
      subtitle: 'Open widget side menu to start drag and drop your prefered charts',
    },
    dashboardChartFilters: {
      agentsUtilizationChart: {
        allAgents: {
          title: 'All Agents Roles',
        },
        buildCaption: {
          title: 'Build Caption',
        },
        buildGroup: {
          title: 'Build Group',
        },
      },
    },
    statusBar: {
      totalBuilds: 'Total No. of Builds',
      withBuildCache: 'With Build Cache',
      withoutBuildCache: 'Without Build Cache',
      totalBuildDuration: 'Total Build Duration',
      avgBuildDuration: 'Avg Build Duration',
      seeOverallAvgAbove: 'See overall Avg above',
      getShorterBuilds: 'Get Shorter Builds with',
      buildCacheButton: 'Build Cache',
    },
    noData: {
      title: 'No Data',
      subtitle: 'This dashboard requires at least one build',
      noResultsFound: 'No Results Found',
      clearFilters: 'Clear Filters',
    },
  },
  agentsTable: {
    backupIsActiveTooltipText: 'This action is available in the primary coordinator',
    notAvailable: 'N/A',
    agentsSelectedCount: 'No agents selected | One agent selected | {count} agents selected',
    agentsFilteredCountA: 'Showing none of | Showing 1 of | Showing {count} of',
    agentsFilteredCountB: 'no agents | 1 agent | {count} agents',
    universalHelperTooltipForInitiator: 'Agent defined as ‘Universal Helpers’ can’t be set as Initiator',
    sclAgentTooltipForInitiator: 'SCL agent can’t be set as Initiator',
    agentsInitiatorLicenseTypes: {
      fixedInitiator: 'Fixed initiator',
      floatingInitiator: 'Floating initiator',
      fixedCIInitiator: 'Fixed CI initiator',
      floatingCIInitiator: 'Floating CI initiator',
      none: 'None',
      '-----------': '-----------',
    },
    agentBuildCacheConfiguration: {
      multipleLabel: '[Multiple values]',
      title: 'Enable Build Cache',
      allowCache: 'Allocate Build Cache',
      localCache: 'Use Local Cache',
      localCacheDisabled32Bit: 'Local cache requires a 64-bit operating system',
      localCacheDisabledCluster: 'Local cache is not supported for machines that are part of a cluster',
      sharedCache: 'Use Shared Cache',
      cacheFolderTitle: 'Cache Folder',
      agentsLocalCacheNote: 'Agents will use local cache in contributor mode',
      agentsSharedCacheNote: 'Agents will use shared cache in contributor mode',
      hint: 'Must be 1GB to 10.000GB',
      validationRules: {
        host: 'Must be valid host or IP',
        port: 'Must be 1024 to 65535',
      },
    },
    csvReportMessage: {
      GeneralError: 'Agent action failed',
      UnsubscribeNotSupportedError: 'Agent skipped - can not release license due to incompatible license version',
      DuplicateLicenseError: 'Agent skipped - Action is not relevant to duplicate agents',
      VersionUpdateIsRequiredError: 'Agent skipped - incompatible version. Update version is required',
      AgentIsNotHelperError: 'Agent skipped - does not have a helper license',
      AgentIsOfflineError: 'Agent skipped - action valid only when agent is online',
      AgentIsNotOfflineError: 'Agent skipped - action valid only when agent is offline',
      VersionUpdateIsNotRequiredError: 'Agent skipped - Update version is not required',
      NotCloudAgentError: 'Agent skipped - Action is relevant only to cloud agents',
      NotDuplicateLicenseError: 'Agent skipped - Action is relevant only to duplicate agents',
      ActionsOnSingleUseAgentAreNotAllowedError: 'Agent skipped - action not allowed on SUVM',
      AgentNotFoundError: 'Agent skipped - Agent not found',
    },
    agentsHelperLicenseTypes: {
      fixed: 'Fixed helper',
      floating: 'Floating helper',
      none: 'None',
      '-----------': '-----------',
    },
    agentsLicenseConfDefaultOption: '-----------',
    fixed: 'Fixed', // fixed license
    floating: 'Floating', // floating license
    none: 'None', // None license
    initiator: 'initiator | initiators',
    helper: 'helper | helpers',
    building: 'Initiating',
    running: 'Running',
    runningBuilds: '{count} builds',
    agents: 'agents',
    coresHelping: 'cores helping',
    runningBuildNoTitle: 'Running build',
    priorityText: {
      success: '{0} priority settings updated',
      assignmentPriorityFail: 'Assignment Priority can only be set on Helper agents',
      buildPriorityFail: 'Build Priority can only be set on Initiator agents',
    },
    setAllowHelperEnabled: {
      success: 'Permission settings updated',
      fail: 'Permission settings failed to update',
    },
    setHelperEnabled: {
      success: 'Helper settings updated',
      fail: 'Helper settings failed to update',
    },
    logLevel: {
      success: 'Log level settings updated',
      fail: 'Log level settings failed to update',
    },
    buildAvoidance: {
      success: 'Build cache settings updated',
      fail: 'Build Cache settings failed to update',
    },
    clearCache: {
      success: 'Clear cache request sent to agent',
      fail: 'Clear cache request failed to send to agent',
    },
    resizeCache: {
      success: 'Cache size updated',
      fail: 'Cache size failed to update',
    },
    agentDescription: {
      success: 'Agent description updated',
      fail: 'Agent description failed to update',
    },
    agentInvalidActionsCSVMessages: {
      duplicateLicense: 'Agent skipped - Action is relevant only to duplicate name agents',
      cloud: 'Agent skipped - Action is relevant only to cloud agents',
      incompatibleVersion: {
        required: 'Agent skipped - incompatible version. Update version is required',
        notRequired: 'Agent skipped - Update version is not required',
      },
      offline: 'Agent skipped - offline state',
      unsubscribeAgent: 'Agent skipped - can not release license due to incompatible license version',
      enableAsHelper: 'Agent skipped - does not have an helper license',
    },
    linuxActionsMenu: {
      serverTitle: 'Subscribe License Cores',
      currentAgentCores: 'Currently subscribed agent cores',
      availableAgentCores: 'Available cores in license',
      coreAssignmentTitle: 'Core assignment',
      assignmentExplanation: 'Subscribe all available cores',
      assignmentFieldLabel: 'Number of cores to subscribe',
      status: {
        ready: 'Online',
      },
    },
    actionsMenu: {
      licenseConfig: 'Set Agent Role',
      setHelperRole: 'Set Helper Role',
      setInitiatorRole: 'Set Initiator Role',
      agentDescription: {
        title: 'Agent Description',
      },
      assignmentPriority: 'Assignment Priority',
      buildPriority: 'Build Priority',
      buildGroup: 'Build Group',
      logLevel: 'Log Level',
      buildCacheConfiguration: {
        title: 'Build Cache Configuration',
        submenus: {
          folderLocation: 'Folder Location',
          cacheSize: 'Cache Size',
          cacheCluster: 'Cache Cluster',
        },
      },
      bulk: {
        buildCache: 'Build Cache Allocation',
        allocateBuildCache: 'Enable Build Cache',
        deallocateBuildCache: 'Disable Build Cache',
        enableAsHelper: 'Enable/Disable as Helper',
        setInitiator: 'Set Initiator',
        setHelper: 'Set Helper',
      },
      cleanCache: 'Clean Helper Cache',
      helperConfigurations: 'Helper Configurations',
      buildCacheClusters: {
        noClusters: 'No Clusters',
        noClustersTableTitle: 'Clusters are sets of Build Cache endpoints that function as a single unit',
        noClustersTableButton: 'Create Cluster',
        noClustersSubTitle: 'Click ‘Manage Clusters’ to create your first Cluster',
        alreadyExistsErrorMessage: 'Cluster already exists',
        invalidInputMessage: 'Invalid input: Colons (:) are not allowed',
        initiatorWarning: 'Initiator Agents are not recommended as cluster nodes',
        buttonTitle: 'Manage Cache Clusters',
        noMatches: 'No matches found',
        none: 'None',
        addAgentsToClusterDialogTitle: 'Add Node(s) to Cluster?',
        addAgentsToClusterDialogSubtitle: 'Initiators using these Build Cache Endpoints will use this Cluster instead',
        removeAgentsFromClusterDialogTitle: 'Remove Node(s) from Cluster?',
        removeAgentsFromClusterDialogSubtitle: 'Removing the last Node from this Cluster will stop the Initiators from using it',
        addButton: 'Add',
        removeButton: 'Remove',
      },
    },
    agentsWorkingStatusText: {
      online: 'Agent Up Time',
      offline: 'Agent Down Time',
    },
    setRegistryKey: {
      title: 'Advanced Configuration',
      validationFailedText: 'The value you entered is not valid, please try again',
      positiveIntegerErrorMessage: 'Only positive integers are valid',
      stringErrorMessage: 'Invalid input. Must be between 1-100 English characters',
      registryNoteFor: {
        MaxParallelLinkTargets: 'If this value is greater than the logical cores of an agent, it will be reduced to match the number of logical cores',
      },
      registryMapping: () =>
        JSON.stringify([
          {
            key: 'CoordHost',
            displayName: 'Coordinator Server',
            description: 'IP or DNS of the Coordinator Server (the host)',
          },
          {
            key: 'CoordPort',
            displayName: 'Coordinator Port',
            description: 'The port of the Coordinator Server',
            errorMessage: 'The input should be an integer between 1024 to 65535',
          },
          {
            key: 'MaxLogSize',
            displayName: 'Maximum Log Size',
            description: 'Defines the max size of the log file. By default there is no limit. This will only affect the logs that tend to be the largest, such as Build Cache and Test Console',
          },
          {
            key: 'OnlyFailLocally',
            displayName: 'Only Fail on Local',
            description: 'When enabled, a task that failed remotely, will be retried again on the local machine',
          },
          {
            key: 'UseMsBuild',
            displayName: 'Enable MsBuild',
            description: 'Defines if Visual Studio builds will run MSBuild.exe (Enabled) or devenv.exe (Disabled)',
          },
          {
            key: 'Use64bitToolset',
            displayName: 'Use 64 Bit Toolset',
            description: 'Run Visual Studio builds using the native 64-bit toolset of Visual Studio, instead of the default 32-bit toolset',
          },
          {
            key: 'ForceCPUCount_WhenInitiator',
            displayName: 'Force CPU Count for Initiator',
            description: 'The max number of tasks that can be executed on the initiator. This requires restarting the Incredibuild agent service',
          },
          {
            key: 'HelperActivityReportTimeout',
            displayName: 'Helper Activity Report Timeout',
            description: 'Disable the helper if it fails to communicate with the initiator after this timeout period. By default, this is 40 seconds',
          },
          // WIN-14964
          {
            key: 'MultiThreadedFileSyncThreads',
            displayName: 'Max file sync threads',
            description: 'The max number of threads that will be used for a single file transfer between an initiator and a helper',
            errorMessage: 'The input should be an integer between 1 to 50',
          },
          {
            key: 'ForceEnglishMSBuildOutput',
            displayName: 'Force English Build Monitor',
            description: 'Force the VS Build Monitor output to be in English',
          },
          {
            key: 'AllowParallelLink',
            displayName: 'Allow parallel linking',
            description: 'Determines if linking steps for different projects will be allowed to run in parallel',
          },
          {
            key: 'MaxParallelLinkTargets',
            displayName: 'Max parallel links',
            description: 'When the Allow parallel linking option is enabled, determines the max number of link steps that can run in parallel',
            errorMessage: {
              single: 'The input should be an integer between 1 to ',
              bulk: 'Minimum value is 1',
            }, // add here the value based on agent logical cores
          },
          {
            key: 'AvoidLocalExec',
            displayName: 'Avoid local',
            description: 'Avoid running tasks locally whenever it is possible to distribute them to helpers',
          },
          {
            key: 'MaxConcurrentPDBs',
            displayName: 'Max concurrent PDB file instances',
            description: 'The number of files (belonging to the same project) that can be built concurrently, 0 means no limit',
            errorMessage: 'The input should be an integer between 1 to 100',
          },
          {
            key: 'PredictedExecutionMode',
            displayName: 'Predictive Execution',
            description: 'When enabled, Visual Studio tasks (compilation, link, RC etc.) will be executed in an optimized order. Supported for VS2010 and higher',
          },
          {
            key: 'BuildMonitorOutputFont',
            displayName: 'Build Monitor Output Font',
            description: "The font that will be used in the Build Monitor's Output display",
          },
        ]),
    },
    licenseConfig: {
      titles: {
        header: 'License Configuration',
        initiator: 'Initiator license',
        helper: 'Helper license',
        helpercore: 'Helper cores registration',
        noresult: 'missing action',
      },
      texts: {
        setAgentAs: 'Set initiator role type to | Set helper role type to',
        helpercoreText: 'Set max cores allocation for use as helpers',
        helpercoreTitle: 'Set max cores to use as helpers',
      },
      initiatorNotes: {
        cooldown: 'NOTE: This license will be frozen for 24 Hours',
        tooltip: 'After setting this role, it cannot be moved to a different Agent for the first 24 hours',
      },
      summaryText: 'This will affect {0}/{1} agents',
      coresAvailabilityTitle: 'Cores Availability',
      toast: {
        good: {
          default: 'Settings updated',
        },
        warning: {
          default: 'The action only partially succeeded',
          pendingQueue: 'Some of the Helper cores were not set as Helpers. Not enough available resources',
        },
        strong: {
          default: 'Action was not successful',
          licenseRanOut: 'Failed to set role, no available resources',
        },
      },
      dialog: {
        csvFileName: 'Configuration Result',
      },
    },
    licenseConfigBulk: {
      titles: {
        initiator: 'Initiator bulk config',
        helper: 'Helper bulk config',
        helpercore: 'Helper cores registration',
        noresult: 'missing action',
      },
      add: 'Add',
      remove: 'Remove',
      addRemoveSubtitle: 'Would you like to Add/Remove your {0}s?',
      roleSelectionTitle: 'Choose your {0}s roles',
      coresSelectionTitle: 'What’s your limit cores capacity in %',
      coresAvailabilityTitle: 'Cores Availability',
      summaryTitle: {
        add: 'This action will change/add your selected agent to',
        remove: 'This action will remove your selected',
        default: 'Action will take place on',
      },
    },
    requestSent: {
      success: 'The request was successfully sent',
      error: 'The operation failed',
    },
    postActionDialogText: {
      toast: {
        success: 'Operation completed successfully',
        error: 'Operation failed, please try again',
      },
      dialog: {
        success: {
          title: 'Yeah!!!',
          body: 'Operation completed successfully',
          btn: 'Get the CSV report',
        },
        warning: {
          title: 'Well...',
          body: 'Operation not completed on {0} out of {1} agents',
          btn: 'Get the CSV report',
        },
        error: {
          title: 'Oh No!',
          body: 'Operation not completed on {0} agents',
          btn: 'Get the CSV report',
        },
      },
    },
    bulkActions: {
      mixType: {
        title: 'Action not allowed',
        body: 'You can only perform bulk operations on agents with the same type and state. You can quickly select compatible agents using the drop-down menu next to the select-all button.',
        // body: 'You can only perform bulk actions on agents with the same version of Incredibuild.',
      },
      mixWithCloud: {
        title: 'Action not allowed',
        body: 'You can only perform bulk actions on agents of the same type. Please only select Cloud or Non-Cloud agents.',
      },
      mixWithState: {
        title: 'Action not allowed',
        body: 'You can only perform bulk actions on agents in the same state. Please only select Online or Offline agents.',
      },
    },
    preTooltipText: {
      pendingQueue: 'Pending Queue',
      licenseRanOut: 'Your licenses has ran out',
    },
    toolTips: {
      pendingQueue: {
        title: 'Pending Queue',
        bodyNo: 'There are no available licenses at this time,  Agents will  automatically be update on release',
        bodyPart: 'Part of the licenses are no available at this time,  Agents will  automatically be update on release',
        footer: 'Your Location',
      },
      licenseRanOut: {
        title: 'License Ran out',
        body: 'Your licenses has ran out. For more licenses, please contact our sales department.',
        footer: '',
      },
      BuildCacheLicenseRanOut: {
        title: 'Build Cache License Ran out',
        body: 'Your Build cache license has ran out.For more licenses, please contact our sales department.',
        footer: '',
      },
    },
    filters: {
      status: {
        title: 'Status',
      },
      cpuLoad: {
        title: 'CPU Load',
      },
      availableCpu: {
        title: 'Available CPU',
      },
      cores: {
        title: 'Agent Cores',
      },
      license: {
        titleWindows: 'Agent Role',
        titleLinux: 'Agent Type',
        AdditionalOptions: {
          BuildCache: {
            all: 'All',
            withBuildCache: 'With Build Cache',
            withOutBuildCache: 'Without Build Cache',
          },
        },
      },
      cache: {
        title: 'Helper Cache Size',
      },
      helpers: {
        title: 'Helper Enabled',
      },
      buildGroups: {
        title: 'Build Groups',
      },
      osVersion: {
        title: 'Operating system',
      },
    },
    multiSelection: () =>
      JSON.stringify({
        online: {
          text: 'Online (on prem)',
        },
        offline: {
          text: 'Offline (on prem)',
        },
        cloud: {
          text: 'Cloud',
        },
        incompatibleVersion: {
          text: 'Incompatible version',
        },
        duplicateLicense: {
          text: 'Duplicate license',
        },
      }),
    duplicateLicense: {
      dialog: {
        title: 'License Conflict',
        body: 'This agent is inactive because there is already a machine with the same name that has a fixed license. Click Approve to release the license from the original machine, or click cancel and disconnect this machine from this Coordinator.',
        okBtn: 'Approve',
      },
    },
    noRole: 'No role',
  },
  validate: {
    isInteger: 'Value must be an integer',
  },
  agents: {
    navBar: {
      searchInput: {
        placeholder: 'Search',
      },
    },
    sortBy: {
      searchInput: {
        placeholder: 'Search',
      },
    },
    dialogs: {
      pleaseHold: {
        title: 'Please Hold',
        body: 'We’re working on it…',
      },
    },
  },
  usersTable: {
    userEdit: {
      success: 'Changes saved',
      unlocked: 'Unlocked successfully',
      fail: 'Action failed',
      editForm: {
        selfStatus: 'You cannot deactivate your own user',
        accountHeader: 'Account',
        detailsHeader: 'Details',
        lastLogin: 'Last login:',
        passwordChangeBtn: 'Change Password',
        passwordChangeCancelBtn: 'Cancel Password Change',
      },
    },
    userRole: {
      success: 'Role changed',
      fail: 'Action failed',
      lastAdmin: 'You cannot perform this action because you need at least one user with the admin role',
    },
  },
  users: {
    userAdded: {
      success: 'New user added',
      fail: 'Failed to create user',
    },
    onlyLocalUsersNote: 'This list only contains users defined in Incredibuild and does not display users defined in your external authentication system',
    userFormRules: {
      password: 'Must be at least 8 characters, with at least one uppercase letter and no spaces',
      userName: 'Must be at least 4 characters, with no special characters or spaces',
      email: 'Invalid email format',
      confirmPassword: 'Passwords do not match',
      required: 'Required field',
      invalid: 'Invalid entry',
    },
    notUniqueUserName: 'Username already exists',
    okCancelDialog: {
      title: 'Save Changes',
      body: 'Do you want to save your settings changes?',
      okLabel: 'Save',
      cancelLabel: "Don't Save",
    },
  },
  settings: {
    summary: {
      windows: {
        agents: {
          title: 'Agents',
          performance: {
            title: 'Helper Participation Threshold',
            diskSpace: { text: 'Min disk space' },
            virtualMemory: { text: 'Min virtual memory' },
            physicalMemory: { text: 'Min physical memory' },
            cpuUsage: { text: 'CPU Usage' },
            coreLimits: { text: 'Core limits' },
            ignoreProcesses: { text: 'Ignore those processes' },
          },
          license: {
            title: 'Agent Assignments',
            floatingReleaseTime: { text: 'Release offline agents' },
            helperCore: { text: 'Helper Core Allocation' },
            baAllocation: { text: 'Auto Build Cache' },
            defaultHelperLicense: { text: 'Default Helper role' },
            defaultInitiatorLicense: { text: 'Default Initiator role' },
            defaultHelperCoresAllocation: { text: 'Helper Cores Allocation' },
          },
          cache: {
            title: 'Helper Cache',
            scheduleTime: { text: 'Clear cache schedule' },
          },
        },
        general: {
          title: 'General',
          network: {
            title: 'Network',
            coordinatorPort: { text: 'Agent comm. port' },
            uiPort: { text: 'UI port' },
            serverPort: { text: 'Server port' },
            licensePort: { text: 'License port' },
            rabbitMqPort: { text: 'Internal Comm. Port' },
            connectivity: { text: 'Connectivity check' },
            prometheusIntegration: { text: 'Prometheus integration' },
            prometheusPort: { text: 'Prometheus Port' },
            encryption: { text: 'Encrypt communication' },
            SSLPort: { text: 'Secured port' },
            SslCnValidationPattern: { text: 'Agent Certificate Validation' },
            communicationError: { text: 'Network is down due to Apache or Coordinator fail' },
            // communication: { text: 'communication' },
          },
          database: {
            title: 'Database',
            timePeriod: { text: 'Time period' },
            hddSizeLimit: { text: 'HDD size limit' },
          },
          system: {
            title: 'System',
            preventSysStandby: { text: 'Prevent standby' },
            dontShowWinFW: { text: 'Hide firewall msgs' },
            helpUsImprove: { text: 'Help us improve' },
          },
          coreLimit: {
            title: 'Core Limit',
            forEachBuild: { text: 'Max per build' },
          },
          userAuth: {
            title: 'User Authentication',
            activeDirectory: { text: 'System' },
          },
          telemetry: {
            title: 'Telemetry',
            helpUsImprove: { text: 'Help us improve' },
          },
          backup: {
            title: 'Backup Coordinator',
            summarySection: {
              title: 'Backup',
              isBackupConfiguredValueTitle: 'Backup coordinator',
            },
            backupSelection: { text: 'A Coordinator to use if your primary Coordinator goes offline.' },
            incompatibleBackupSelectionErrorMessage: 'NOTE: Incredibuild versions of backup and main do not match. Upgrade required on backup',
            noBackupText: 'Currently there are no Backup Coordinators configured.',
            actionButton: { text: 'Refresh' },
            learnMore: {
              url: 'https://docs.incredibuild.com/win/latest/windows/using_backup_coordinator.html',
              text: 'Learn More',
            },
          },
        },
        apiAccess: {
          title: 'API Access',
          activeApiKeys: {
            title: 'Active API Keys',
          },
        },
        cloud: {
          title: 'Cloud',
        },
      },
      linux: {
        general: {
          title: 'General',
          system: {
            title: 'System',
            coresPerBuild: { text: 'Max cores per build' },
            maxCPU: { text: 'Max CPU load' },
            minMemory: { text: 'Min free memory' },
            reloadTimeout: { text: 'Machine  reload timeout' },
            autoSubscribe: { text: 'Auto-subscribe new agents' },
            telemetry: { text: 'Help us improve' },
          },
          network: {
            title: 'Network',
            controlPort: { text: 'Agent control port' },
            sessionPort: { text: 'Coordinator session port' },
            utilityPort: { text: 'Coordinator utility port' },
            managerPort: { text: 'UI port' },
            ipv6: { text: 'IPv6 network' },
            webserverPort: { text: 'Agent webserver port' },
            webserverSSLPort: { text: 'HTTPS agent webserver port' },
          },
          ssl: {
            title: 'SSL',
            sslValue: { text: 'Enable SSL' },
            useCoordinatorAgentTunnel: { text: 'Agent UI via coordinator HTTPS' },
          },
          userAuth: {
            title: 'User Authentication',
            activeDirectory: { text: 'System' },
          },
          backup: {
            title: 'Secondary Coordinator',
            hostname: { text: 'Hostname or IP' },
            port: { text: 'Port' },
            timeout: { text: 'Timeout' },
            syncFrequency: { text: 'Sync frequency' },
          },
        },
        apiAccess: {
          title: 'API Access',
          activeApiKeys: {
            title: 'Active API Keys',
          },
        },
        buildGroups: {
          title: 'Build Groups',
          buildGroupsData: {
            title: 'Existing build groups',
            numOfBuildGroups: { text: 'Number of build groups' },
          },
        },
      },
    },
    dialogs: {
      addAgentsForFreeDevLicense: {
        title: 'Add Agents',
        body: 'To add a machine as an Agent, share the link below with the machine owner',
        linkText: 'Copy Invite Link',
      },
      connectActiveDirectory: {
        title: 'Authentication System',
        editNote: 'To modify the disabled fields, disconnect from your current connection and establish a new one',
        activeDirectory: {
          ldap: {
            text: 'LDAP Server & LDAP Port',
            tooltip: 'The fully qualitified domain name of your LDAP server. If you want to use an IP instead of the machine name, make sure the IP appears in your certificate as an alternative name',
          },
          lpadAuthority: {
            text: 'Root certificate of CA',
            tooltip: 'The root certificate of the certificate authority used to create your certificates. If you have a certificate chain, read the documentation for more information',
          },
          bindDN: {
            text: 'Bind DN',
            tooltip: 'The distinguished name (DN) used to authenticate and perform operations in the LDAP directory',
          },
          bindCredentials: {
            text: 'Bind Password',
            tooltip: 'The password associated with the Bind DN',
          },
          groupDN: {
            text: 'Group DN',
            tooltip: 'The Distinguished Name (DN) to use when Searching for Groups in Active Directory. This is used to allow you to manage permissions on the group level in Incredibuild',
          },
          searchBase: {
            text: 'User DN',
            tooltip: 'The Distinguished Name (DN) to use when Searching for Users in Active Directory. This is required to validate users when logging in to Incredibuild',
          },
          usernameAttribute: {
            text: 'User ID Attribute',
            tooltip: 'The specific attribute that represents the user ID in the LDAP directory',
          },
        },
        azureActiveDirectory: {
          tenantId: {
            text: 'Tenant ID',
            tooltip: {
              a: 'The unique identifier for your Azure',
              b: 'Active Directory (AAD) tenant',
            },
          },
          clientId: {
            text: 'New Azure Application ID',
            tooltip: {
              a: 'The Application ID assigned to',
              b: 'your new Azure AD application',
            },
          },
          clientSecret: {
            text: 'Client Secret',
            tooltip: 'A confidential key used to authenticate your application',
          },
        },
        manageSecurityGroups: {
          errorMessageInGroupsList: {
            a: 'There were errors in ',
            b: 'Review the highlighted fields for more details.',
          },
          numberOfGroupsIsTooLargeToDisplayWarning: {
            a: 'The group list cannot be displayed due to an LDAP error: ',
            b: 'large group list, incorrect query, or other issue.',
            c: 'Enter the exact group name below to assign a role.',
          },
          forbiddenErrorMessageInGroupsList: {
            a: "You don't have permission to access security groups in",
            b: 'the Authentication System',
          },
          disabledDialogTooltip: 'You need to define a Group DN by clicking Edit above. This will allow Incredibuild to manage permissions based on your Active Directory Groups',
          unauthorizedTooltip: "You don't have permission to access security groups in the Authentication System",
          ldapNotConnectedTooltip: 'Your authentication system was offline. Disconnect and reconnect in Incredibuild when it is back online',
          permissionDeniedTooltip: "You don't have permission to access security groups in the Authentication System",
          noGroupWaringNote: "If you don't define security groups, users imported from your Authentication System will not have permissions in Incredibuild.",
          title: 'Manage Security Groups',
          subtitle: 'Define which security groups obtain which roles in Incredibuild. You must have at least one group that receives the Grid Admin role',
          localSubtitle: 'To configure this integration, you need to log in to Azure:',
          successToast: 'Security Groups defined',
          errorToast: 'Failed to define Security Groups',
          groupErrors: {
            noChosenGroup: 'Select a security group',
            groupAlreadyMapped: 'is assigned more than once',
            noGroupFound: 'The selected group is no longer available. It may have been deleted or renamed. Choose a different group',
          },
        },
        errorNote: {
          general: {
            a: 'Connection failed.',
            b: 'Please contact your system administrator for assistance',
          },
          certificateFailed: {
            a: 'Unable to validate LDAPS certificate using',
            b: 'the root certificate you provided',
          },
        },
        successToast: {
          activate: 'External user authentication connected',
          deactivate: 'External user authentication disconnected',
        },
        formValidations: {
          range: 'Port must be 1 to 65535',
          required: 'Required field',
          invalid: 'Invalid field',
        },
        footer: {
          activate: 'Connect',
          edit: 'Edit',
          define: 'Define',
          deactivate: 'Disconnect',
          cancel: 'Cancel',
        },
      },
      pleaseHoldCoordinatorServerPort: {
        title: 'Please Wait',
        body: 'The coordinator server port change is being processed...',
        footer: 'This pop-up will close up automatically when this action is done',
      },
      Port: {
        title: 'Agent communication port change!',
        body: 'Agent communication has been changed. <br/> Please close & reopen your browser to open the new Incredibuild coordinator.',
      },
      DashboardPort: {
        title: 'Redirect to the Coordinator',
        body: 'The Coordinator UI Port has changed, which means <br/> you need to reconnect using the new port. Close and reopen this browser using the new port number, or just click Redirect.',
      },
      CoordAPIPort: {
        title: 'Coordinator server port change!',
        body: 'Coordinator server port has been changed. <br/> Please close & reopen your browser to open the new Incredibuild coordinator.',
      },
      LicensePort: {
        title: 'License Port change!',
        body: ' License Port has been changed. <br/> Please close & reopen your browser to open the new Incredibuild coordinator.',
      },
      SSLPort: {
        title: 'SSLPort change!',
        body: 'SSLPort has been changed. <br/> Please close & reopen your browser to open the new Incredibuild coordinator.',
      },
      okCancelDialog: {
        title: 'Save Changes',
        body: 'Do you want to save your settings changes, before leaving this page?',
        okLabel: 'Yes',
        cancelLable: 'No',
      },
    },
    notifications: {
      settingsSavedSuccessfully: {
        text: 'Changes saved successfully',
      },
      settingsSavedUnSuccessfully: {
        text: 'Unable to save changes',
      },
    },
    general: {
      updates: {
        title: 'Updates',
        CheckForUpdatesOnline: {
          text: 'Enable automatic version updates',
        },
      },
      coreLimit: {
        title: 'Core limit',
        forEachBuild: { text: 'Maximum cores to use for each build' },
      },
      network: {
        title: 'Network',
        portsRangeValueErrorMessage: 'Must be 1024 to 65535',
        portsPositiveRangeValueErrorMessage: 'Must be 1 to 65535',
        portsAvailabilityValidationErrorMessage: 'Port already in use',
        port: {
          text: 'Agent communication port',
          tooltip: 'Enables the coordinator to receive updates from agents in the network',
        },
        coordPort: {
          text: 'Coordinator UI port',
          tooltip: 'The port on which the web UI of the coordinator will be available.',
        },
        coordServerPort: {
          text: 'Coordinator server port',
          tooltip: 'Enables the coordinator interface to communicate with the coordinator server',
        },
        licensePort: {
          text: 'License port',
          tooltip: 'Enables license management and related operations to be facilitated',
        },
        rabbitMqPort: {
          // Internal communication port
          text: 'Internal communication port',
          tooltip: 'Message transport port for internal communication between Coordinator components',
          descriptionText: 'To update it, contact ',
        },
        reportInterval: {
          text: 'Agent status report every:',
          tooltip: 'How often the Agent reports their status to the Coordinator. Recommended: 10 sec. Max: 59 sec.',
        },
        coordinatorApiAccess: {
          text: 'Coordinator API access',
        },
        prometheusPort: {
          tooltip: 'Prometheus integration is part of the Enterprise edition features',
          enableTitle: 'Enable Prometheus integration',
          text: 'Prometheus Port',
          linkText: 'Prometheus integration',
          descriptionA: 'The port used to communicate with your',
          descriptionB: 'You also need to ensure that Prometheus can communication on this port as well',
          url: 'https://docs.incredibuild.com/win/latest/windows/prometheus.html',
        },
        agentsUseSecureConnections: {
          text: 'Encrypt communication',
        },
        securedPort: {
          text: 'Secured port',
          tooltip: 'Used for encrypted communication between Incredibuild components',
        },
        agentCertificateValidation: {
          title: 'Agent certificate validation',
          description: 'A regular expression used to validate agent common names in your certificate',
          tooltip:
            'If left blank, your SSL certificates will ignore the agent names during validation. To validate the names as well, define a regular expression that matches the common names in your agent certificates',
        },
      },
      database: {
        title: 'Database',
        timePeriod: {
          text: 'Time period',
        },
        hddSizeLimit: {
          text: 'HDD size limit',
        },
      },
      telemetry: {
        title: 'Telemetry',
        subtitle: 'Automatically collect information to help us improve quality and performance',
        tooltip:
          'When this option is enabled, Incredibuild collects statistical information as stated in the online Privacy Policy. This in no way affects performence execution, and no personal information is being sent. You can disable this option at any time.',
        telemetryEnabled: {
          text: 'Help us improve our products & services',
        },
      },
      system: {
        title: 'System',
        allowSystemStandby: {
          text: 'Prevent system standby state while service is running',
        },
        ignoreFirewall: {
          text: 'Don’t show Windows Firewall related messages',
        },
        helpUsImprove: {
          text: 'Help us improve our products & services',
        },
        allowCustomDistributionProfiles: {
          text: 'Allow changes to default distribution profile',
          tooltip:
            'In order to support some technologies, Incredibuild can use custom profiles to modify its distribution behavior. Checking this allows end users to choose which tools can run remotely on the grid',
        },
      },
      noConfiguredBackupOption: 'None',
      backup: {
        title: 'Secondary Coordinator',
        subTitle: 'A Coordinator to use if your primary Coordinator goes offline.',
      },
      userAuth: {
        title: 'User Authentication',
        subTitle: 'Integrate with user authentication system',
        text: 'Manage user authentication with an external tool such as Active Directory or Azure Active Directory',
      },
      linux: {
        general: {
          ssl: {
            title: 'SSL',
            tooltip: 'Sets the operating mode for the whole grid. When checked SSL is used',
            useCoordinatorAgentTunnel: {
              title: 'Access agent UI via coordinator HTTPS',
              description: 'Traffic to Agent machines is rerouted through your Coordinator HTTPS, enabling access to the Agent UI without the need to install certificates on the Agent machines.',
            },
          },
        },
        tooltipText: {
          coordinator: {
            buildMaxCores:
              'The maximum number of cores across the IncrediBuild grid that can be used per build. The default -1 value instructs IncrediBuild not to limit the number of cores per build.',
            agentMaxCpuLoad: 'The Coordinator will not allocate Agents which their CPU load exceeds the defined value.',
            agentMinFreeMemory: 'The Coordinator will not allocate Agents which their free memory (per cpu) is below the defined value.',
            agentReloadTimeout: 'The Coordinator will not allocate Agent which reported higher CPU than "Max CPU Load" for "Machine Reload Timeout" seconds.',
            autoSubscribeNewAgents: 'Automatically subscribe new Agents to the Coordinator. Modifying this setting does not affect currently connected Agents.',
            agentControlPort: 'This port is used for communication between the Initiator and the Coordinator.',
            sessionPort: 'This port is used to create sessions.',
            utilityPort: 'This port is used for Agents registration and keep-alive mechanism.',
          },
          network: {
            ipv6: 'Sets the operating mode for the whole grid. When unchecked IPv4 is used. All the agents connected to the coordinator should be compliant with the selected IP version.',
            ssl: 'Sets the operating mode for the whole grid. When checked SSL is used.',
            webserverPort: 'This port is used to communicate with the webserver that is responsible for accessing the user interface.',
            httpsWebserverPort: 'This port is used to communicate with the webserver that is responsible for accessing the user interface.',
          },
          backup: {
            backupCoordinatorHostname: 'The hostname or IP of the Secondary Coordinator.',
            backupCoordinatorPort: 'The port used to connect to the Secondary Coordinator',
            backupCoordinatorTimeout: 'The secondary Coordinator will be used if the primary Coordinator is not responsive for the timeout defined.',
            backupCoordinatorSyncFrequency: 'The frequency of syncing the data from the primary coordinator to the secondary.',
          },
        },
        validationsErrors: {
          positiveNumbers: 'Must be zero or above',
          range: 'Must be between 0-100',
          backupTimeoutMinimum: 'Min value is 30 seconds',
          portInUse: 'This port is already in use',
        },
      },
    },
    agents: {
      agentHelperPermissions: {
        title: 'Agent Helper Permissions',
        subtitle: 'Defines whether helper agents are allowed to enable/disable \n themselves as helpers by default. Applies to new agents.',
        labelText: 'Allow agents to "Enable/Disable as Helper"',
        summaryTitle: 'Allow to "Enable/Disable"',
      },
      helperAssignment: {
        title: 'Helper Participation Threshold',
        subtitle: 'To ensure that helper machines are not overused by Incredibuild, helpers only assist when the following resources are available:',
        ignoreCpuProcess: {
          text: 'Ignore those processes in CPU count',
        },
      },
      license: {
        title: 'Agent Assignments',
        subtitle: 'Releasing Offline Agents',
        helperText: 'Release floating licenses when agent is offline for:',
        placeholder: 'If an agent with a floating license is offline, we release the license so it can be used by other agents.',
      },
      buildAvoidnace: {
        title: 'Build Cache',
        subtitle: 'Set Automatic Build Cache Allocation to floating initialtor agents',
        automaticAllocate: {
          text: 'Automatic Build Cache Allocation',
        },
      },
      diskSpace: {
        title: 'Disk space',
        text: 'Helper will not be available for work above chosen Disk space',
      },
      memory: {
        virtual: {
          title: 'Virtual memory',
        },
        physical: {
          title: 'Physical memory',
        },
      },
      coreLimit: {
        title: 'Available CPU',
        subtitle: 'Choose core limit for <br/> each build',
      },
      LastCacheCleared: {
        title: 'Helper Cache Cleanup',
        subtitle: 'Schedule a periodic cleanup',
        clearCache: {
          text: 'Clean Now',
        },
        RunCacheCleanup: {
          text: 'Schedule cleanup every',
        },
      },
      cpuHelperCoresRegistration: {
        title: 'Default Allocation of Helper Cores',
        subtitle: 'Default percentage of cores on each agent that can be used as Helpers',
      },
      defaultAgentLicense: {
        title: 'Default Roles for New Agents',
        initiatorMenuTitle: 'Initiator',
        helperMenuTitle: 'Helper',
      },
      removeFloatingOfflineAgents: {
        title: 'Retain Offline Agents',
        subtitle: 'Offline floating agents are automatically removed from the Agent List. By default, this occurs within one minute, but you can retain them for longer using this option.',
        removeAgents: {
          text: 'Retain offline agents for',
          hint: 'Must be 1 to 60',
        },
        summaryText: 'Retain for',
      },
    },
    apiAccess: {
      title: 'API Access',
      actionToasts: {
        description: {
          success: 'Changes saved',
          fail: 'Failed to change the description, try again',
        },
        changeRole: {
          success: 'Role changed',
          fail: 'Action failed',
        },
        deleteKey: {
          success: 'Token revoked',
          fail: 'Action failed, please try again',
        },
      },
      description: "Create an API Key to use Incredibuild's public APIs",
      coordinatorId: {
        descriptionA: 'Incredibuild’s API call requires you to send the specific',
        descriptionB: 'Coordinator’s ID',
      },
      generateButton: 'Generate API Key',
      addNewKeyDialog: {
        buttonTitle: 'Add Key',
        hintMessage: 'Key description should be between 4 to 25 characters',
        successToastMessage: 'Key generated',
        failToastMessage: 'Failed to generate key',
      },
      replaceButton: 'Replace API Key',
      replaceDialog: {
        title: 'Relpace API Key',
        description: 'Are you sure you want to replace your API key? Any API calls using the previous key will no longer work',
      },
      deleteDialog: {
        title: 'Delete API Key',
        description: 'Are you sure you want to delete your API key? Any API calls using the previous key will no longer work',
      },
    },
    buildGroups: {
      title: 'Build Groups',
      actionToasts: {
        description: {
          success: 'Changes saved',
          fail: 'Failed to change build group, try again',
        },
        deleteBuildGroup: {
          success: 'Changes saved',
          fail: 'Action failed, please try again',
        },
      },
      addNewBuildGroupDialog: {
        buttonTitle: 'Add Build Group',
        successToastMessage: 'Build group created successfully',
        failToastMessage: 'Failed to create new build group',
      },
      deleteDialog: {
        title: 'Delete Build Group',
        description: 'Are you sure you want to delete this Build Group?',
      },
    },
    cloud: {
      status: {
        Creating: 'Creating pool...',
        Deleting: 'Deleting pool...',
        Deactivating: 'Deactivating pool...',
        InProcess: 'Operation in process...',
        Waiting: 'Please wait...',
      },
      dialogs: {
        pause: {
          title: 'Pause Cloud',
          body: 'You’re about to pause your cloud, Would you like to delete your resources with it?',
          checkboxText: 'Yes. delete my VMs',
          error: 'Error pausing cloud',
        },
        deactivate: {
          title: 'Deactivate Cloud',
          body: 'You’r about to deactivate your cloud and delete all of its resources, as VMs, permissions, tokens and volumes.',
          checkboxVerifyText: 'Yes. I want to deactivate.',
          error: 'Error deactivating cloud',
        },
        resume: {
          error: 'Error resuming cloud',
        },
      },
      cloudDisabled: {
        title: 'Unlimited Acceleration of Your Development',
        body: {
          p1: 'Incredibuild Cloud offers you the ability to instantly scale up to thousands of cores for accelerating your development tasks, by combining Incredibuild virtualization technology within cloud services.',
          p2: 'You can add additional CPU power that is needed on demand to accelerate builds, testing, graphics, and any other development processes you might have as part of your build.',
        },
        button: 'Add Cloud Helpers',
        tooltipTextA: 'Create helper agents in the cloud.',
        tooltipTextB: 'Get your free trial today',
        learnMoreLink: 'Learn More',
        contactSalesLink: 'Contact sales',
        tooltipMessage: 'This option is only available when accessing the Coordinator directly from the Coordinator machine (not remotely).',
      },
      cloudEnabled: {
        statusText: 'Cloud Enabled',
        secondaryStatus: 'Infrastructure is ready, you can start using Incredibuild Cloud',
        ctaButtonText: 'Cloud Settings',
      },
      cloudPaused: {
        statusText: 'Cloud Paused',
        secondaryStatus: 'Cloud was paused',
        ctaButtonText: 'Resume Cloud',
      },
      cloudExpired: {
        statusText: 'License expired',
        secondaryStatus: 'License has expired, Contact sales to resume',
        ctaButtonText: 'Contact sales to resume',
      },
    },
  },
  license: {
    title: 'License',
    refreshLicenseSuccess: 'Operation to refresh license succeeded',
    refreshLicenseFailed: 'Operation to refresh license failed',
    dialog: {
      deactivate: {
        title: 'Deactivate Incredibuild',
        body: 'This will disable your Coordinator license, effectively preventing all Agents from distributing builds.',
        okLabel: 'Continue',
        cancelLabel: 'Close',
        verifyText: 'Yes. I want to deactivate',
      },
      deactivationFailed: {
        title: 'Deactivation Failure',
        body: 'We could not complete the deactivation due to connection errors',
        okLabel: 'Contact us',
        cancelLabel: 'Close',
      },
    },
    deactivate: {
      windows: {
        title: 'Deactivate',
        subtitle: 'Deactivate your license on this machine. This let’s you activate it on another machine.',
        button: 'Deactivate',
      },
      linux: {
        title: 'Unload License',
        subtitle: 'If you have received an updated license file, you need to unload the original license before you can load the new one',
        button: 'Unload',
      },
    },
    dashboard: {
      helpers: {
        title: 'Helper Cores',
      },
      initiators: {
        title: 'Initiators',
      },
      buildAvoidance: {},
    },
    addons: {
      title: 'Packages and Features',
    },
    totalDetailedView: {
      initiators: 'Initiators',
      ciInitiators: 'CI Initiators',
      buildCache: 'Cache Cores',
      helperCores: 'Helper Cores',
    },
    uploadFileComponent: {
      generateKey: 'Generate Key File',
      updateLicense: 'Update License',
    },
    renewal: 'License key renewed: The new contract you purchased will be loaded as soon as this one expires',
  },
  userProfile: {
    saveBtn: 'Save',
    failureToastText: 'Action failed',
    personalInfoTab: {
      title: 'Personal Info',
      successToastText: 'Changes saved',
      emailFormatValidation: 'Invalid email format',
      invalid: 'Invalid entry',
    },
    preferencesTab: {
      title: 'Preferences',
      subTitle: 'Reset all saved filters and table settings to factory defaults',
      successToastText: 'Preferences have been reset to factory defaults',
      errorToastText: 'An error occurred while resetting preferences',
      buttonTitle: 'Reset All Preferences',
    },
    changePasswordTab: {
      title: 'Change Password',
      validationMessages: {
        requiredField: 'This field is required',
        newPassword: 'Must be at least 8 characters, with at least one uppercase letter and no spaces',
        confirmPassword: 'Passwords do not match!',
      },
      successToastText: 'Password changed',
    },
    myProfileMenu: {
      title: 'Profile Settings',
      logOut: 'Logout',
    },
  },
  workloads: {
    title: 'Build List',
    summary: {
      buildTitle: 'Total Builds',
      successBuilds: 'Successful Builds',
      totalInitiatorAgents: 'Total Initiator Agents',
      '50P': 'P50 Duration',
      '90P': 'P90 Duration',
      emptyPercentile: {
        a: 'This data requires',
        b: 'at least 10 builds',
      },
    },
    details: {
      title: 'Build Report',
      header: {
        startTime: 'Start Time',
        duration: 'Duration',
        initiator: 'Initiator',
        loggedOnUser: 'Logged on User',
        project: 'Project',
        buildType: 'Build Type',
      },
      summaryTab: {
        title: 'Summary',
        details: {
          title: 'Build Details',
          workingDirectory: 'Working Directory',
          target: 'Target',
          version: 'IB Version',
          totalTasks: 'Total Number of Tasks',
          logsFolder: 'Logs Folder',
        },
        time: {
          title: 'Build Time',
          duration: 'Duration',
          startTime: 'Start Time',
          endTime: 'End Time',
        },
        initiator: {
          title: 'Initiator Details',
          buildGroup: 'Build Group',
          agent: 'Agent Description',
          logLevel: 'Log Level',
        },
        errors: {
          title: 'Errors and Warning',
          failureReason: 'Failure Reason',
          buildSystem: 'Build System',
          ibSystem: 'Incredibuild System',
          errors: 'Errors',
          warnings: 'Warnings',
        },
        configurations: {
          title: 'Configurations',
          buildCache: 'Build Cache',
          cloudHelpers: 'Cloud Helpers',
          predictiveExecution: 'Predictive Execution',
          yes: 'Yes',
          no: 'No',
        },
      },
      performanceMetricsTab: {
        title: 'Performance Metrics',
        tasks: {
          title: 'Tasks',
          totalTasks: 'Total Tasks',
          executedFromCache: 'Cache Hits',
          executedRemotely: 'Executed Remotely',
          executedLocally: 'Executed Locally',
          distributableTasks: 'Distributable Tasks',
          nonDistributableTasks: 'Non Distributable Tasks',
          success: 'Success',
          warning: 'Warning',
          failure: 'Failure',
          cache: 'Cache',
          remote: 'Remote',
          local: 'Local',
          longestTasks: {
            title: 'Longest Tasks',
            taskId: 'Task ID',
            taskMachineName: 'Agent Name',
            taskName: 'Task Name',
            percentage: '% of Build Duration',
            duration: 'Duration',
          },
        },
        buildStructure: {
          title: 'Build Structure',
        },
        buildCache: {
          title: 'Build Cache',
          cacheable: 'Cacheable Tasks',
          uncacheable: 'Uncacheable Tasks',
          hits: 'Hits',
          misses: 'Misses',
          readMore: 'Read More',
        },
        helperDistribution: {
          title: 'Helper Distribution',
          cpuTime: 'Distributed CPU Time',
          cpuTimeTooltip: 'Percentage of the build’s total CPU time that was run on remote helpers',
          unmetHelperRequests: 'Unmet Helper Requests',
          unmetHelperRequestsTooltip: 'The percentage of time when the number of requested helpers is greater than the number of assigned helpers',
          helperAgents: 'Helper Agents',
          helperCores: 'Helper Cores',
          max: 'Maximum',
          avg: 'Average',
        },
      },
    },
    manageColumns: {
      unmetHelperRequestsTooltip: {
        a: 'The percentage of time when the number of requested',
        b: 'helpers is greater than the number of assigned helpers',
      },
    },
    workloadsTable: {
      emptyColumnsFilter: 'This view requires at least one column',
      searchInputLabelWindows: 'Search by Command, Initiator and Build title',
      searchInputLabelLinux: 'Search by Command, Initiator and Build caption',
      unmetHelperRequestsTooltip: {
        a: 'Unmet Helper Requests - The percentage of time when the',
        b: 'number of requested helpers is greater than the',
        c: 'number of assigned helpers',
      },
      noBuildsFound: {
        title: 'No Builds',
        a: 'This view requires at least one build',
        buttonText: 'Clear Filters',
      },
      outOfRangeBuildsDate: {
        title: 'No Builds',
        subtitle: 'Please choose a date within the last 14 days',
        buttonText: 'Display last 14 days',
      },
      noAvailableData: 'Additional data is not available for this build',
    },
    exportCSV: {
      exportFailedMessage: 'CSV download failed',
      exportFailedTooManyRequests: 'CSV download failed: Another download is already in progress',
    },
  },
  helpMenu: {
    copiedCoordinatorIdToClipboard: 'Copied',
  },
  gettingStarted: {
    title: 'Getting Started',
    steps: {
      agents: {
        title: 'Scale Your Incredibuild Grid',
        subSteps_zero_free_dev: 'Add a Helper Agent',
        subSteps_zero: 'No Agents joined',
        subSteps_one: '{ count } of { total} Agents joined',
        subSteps_two: '{ count } of { total} Agents joined',
        subSteps_other: '{ total} or more Agents joined',
        content_1: `Incredibuild distributes builds from Initiator machines to available machines - we call them Helper Agents.
        Helpers boost CPU power for the build process - without disrupting their primary tasks. 
        We recommend adding at least three Helper Agents.`,
        content_2: `Install Incredibuild on additional machines to add them as Helpers and/or Initiator Agents to your grid.
To add a machine, share the link below with the machine owner.`,
        content_2a: `For more details, watch this `,
        content_2_video: `video`,
        content_2b: ` about Incredibuild Technology`,
        content_3: `To add a machine as an Agent, share the link below with the machine owner`,
        link: 'Agent invite link',
        troubleshootingButton: 'Troubleshooting',
        troubleshootingContent: `If the link is not accessible from the machine being added, replace the host in the URL with the correct IP address 
        of the Coordinator machine`,
      },
      builds: {
        title: 'Run Builds',
        subSteps_zero: 'No builds completed',
        subSteps_one: '{ count } of 5 builds completed',
        subSteps_two: '{ count } of 5 builds completed',
        subSteps_three: '{ count } of 5 builds completed',
        subSteps_four: '{ count } of 5 builds completed',
        subSteps_other: '5 or more builds completed',
        tabs: {
          ide: {
            title: 'Visual Studio IDE',
            content_1a: `In Visual Studio choose `,
            content_1b: `Extensions > Incredibuild > Rebuild Solution`,
            content_1c: ` from the menu.`,
            content_2: `Run a build twice. The first build populates the Build Cache, the second build demonstrates the acceleration you will see when a build is already in the cache. Note: Build Cache uses /Z7 rather than the default /Zi.`,
          },
          cmd: {
            title: 'Visual Studio CMD',
            content_1a: `Instead of MSbuild command, use the Incredibuild `,
            content_1b: `BuildConsole`,
            content_1c: ` command:`,
            content_2: `BuildConsole <solution> /cfg=<configuration> /rebuild /openmonitor`,
            content_3a: `For example: `,
            content_3b: `BuildConsole MySolution.sln /cfg="Debug|Win64" /rebuild /openmonitor`,
            content_4: `Run a build twice. The first build populates the Build Cache, the second build demonstrates the acceleration you will see when a build is already in the cache. Note: Build Cache uses /Z7 rather than the default /Zi.`,
          },
          ninja: {
            title: 'Make/Ninja',
            content_1a: `Instead of make or ninja, use the Incredibuild `,
            content_1b: `BuildConsole`,
            content_1c: ` command:`,
            content_2: `BuildConsole /command="<original make/ninja command>" /openmonitor`,
            content_3a: `For example: `,
            content_3b: `BuildConsole /command="make --file=makefile.mak -B" /openmonitor`,
            content_4: `Run a build twice. The first build populates the Build Cache, the second build demonstrates the acceleration you will see when a build is already in the cache. Note: Build Cache uses /Z7 rather than the default /Zi.`,
          },
          other: {
            title: 'Other',
            content_1a: `Consult Incredibuild `,
            content_1b: `documentation`,
          },
        },
      },
      license: {
        title: 'Buy Incredibuild License',
        content: `Your Incredibuild free-trial will expire in {days} days.
        To purchase Incredibuild and continue to accelerate your builds, contact our sales at `,
      },
    },
  },
  installAgent: {
    title: 'Install Agent',
    header: 'Accelerate Builds with Incredibuild',
    description:
      ' invites you to join the acceleration grid. Incredibuild speeds up builds with caching and distributed computing, shortening wait times without changing your process, code, or tools. ',
    joinGrid: 'Join the Grid',
    helper: {
      title: 'As a Helper',
      line1: 'Your CPU will only be utilized when idle,',
      line2: 'with no disruption to your work.',
    },
    initiator: {
      title: 'As an Initiator',
      line: 'Use the grid to accelerate your own builds.',
    },
    downloadButton: 'Download Installer',
    buttonDescription: 'To join the grid, download and run the Agent Installer',
    thankYou1: 'Thank you for helping enhance development efficiency!',
    thankYou2: 'For more info, visit ',
    site: 'incredibuild.com',
  },
  changePassword: {
    body: "You've logged in with a default password. For your account's safety, please change your password immediately",
    actionButton: 'Change Password',
  },
};
